/**
 * "All Deals" (owner request, 2026-08-10) — a banner shortcut that lands straight on the IOT deal
 * list instead of making the operator go CRM → Lists → IOT every time.
 *
 * It is NOT a new surface. It renders the ordinary `CrmPage` and only seeds that page's EXISTING
 * selection param on arrival, so the URL is the same shape any CRM deep link uses
 * (`/all-deals?sel=list:<id>`) and every downstream behavior — the grid, the record panel, Back,
 * reload, copy-a-link — is the CRM page's, unchanged. Consequences of that choice:
 *
 *  - **No second navigation path.** The seed goes through `useSelectionParam('sel').open(...)`, the
 *    same call `CrmListsHome`'s row click makes. No hand-rolled pushState (frontend rules).
 *  - **The seed is a `replace`, not a push.** The TabBar click already pushed the entry for "I went
 *    to All Deals"; pushing again would make Back land on an empty All Deals shell that immediately
 *    re-seeded, i.e. a Back button that appears broken.
 *  - **The list is resolved by KIND, never by id.** Prod's IOT happens to be list 1; dev's is not.
 *  - **It seeds once per mount and only when `sel` is absent.** An arriving deep link
 *    (`/all-deals?sel=person:7`, or a reload after drilling into a person) is honored as-is, and
 *    closing the grid genuinely returns to the lists index rather than being yanked back.
 *  - **While the lists load — or if they fail, or the caller can see no deal-pipeline list — the
 *    CRM page renders its normal lists index.** Never a blank pane, never a spinner of our own that
 *    could outlive the request.
 *
 * Visibility is the CRM tab's, enforced server-side: `/api/tabs` gates key `all-deals` through the
 * identical `_can_use_crm_tab` branch as `crm` (`backend/app/routers/tabs.py`), and every CRM
 * endpoint this page calls stays independently governed.
 */
import { useEffect, useRef } from 'react';

import { findDealPipelineList, pushRecentList } from '../crm/crmListsIndex';
import { useSelectionParam } from '../../hooks/navigation/useUrlState';
import { useCrmLists } from '../../hooks/useCrm';
import { CrmPage } from './CrmPage';

export function AllDealsPage() {
  const lists = useCrmLists();
  const { value: selValue, open: openSel } = useSelectionParam('sel');
  // One seed per mount. Guarded by a ref rather than by "is sel empty right now", so closing the
  // grid (which drops `sel`) is not immediately undone by a re-seed.
  const seeded = useRef(false);

  useEffect(() => {
    if (seeded.current) return;
    // An explicit selection arrived with the URL (deep link, reload, MSAL restore) — honor it and
    // never overwrite it with the deal list.
    if (selValue) {
      seeded.current = true;
      return;
    }
    // Still loading / errored / nothing visible: leave the URL alone. CrmPage shows the lists index,
    // and a later successful load re-runs this effect.
    const target = findDealPipelineList(lists.data?.lists);
    if (!target) return;
    seeded.current = true;
    pushRecentList(target.id);
    openSel(`list:${target.id}`, { replace: true });
  }, [lists.data, selValue, openSel]);

  return <CrmPage />;
}
